import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from "lucide-react";
import { cn } from "./cn";

const ToastContext = createContext(null);

let counter = 0;
const nextId = () => `t_${Date.now()}_${++counter}`;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((opts) => {
    const id = nextId();
    const toast = {
      id,
      tone: opts.tone || "default",
      title: opts.title || "",
      description: opts.description || "",
      duration: opts.duration ?? 4500,
    };
    setToasts((prev) => [...prev, toast]);
    if (toast.duration > 0) {
      window.setTimeout(() => dismiss(id), toast.duration);
    }
    return id;
  }, [dismiss]);

  const value = React.useMemo(
    () => ({
      toast: push,
      success: (title, description, opts = {}) => push({ ...opts, tone: "success", title, description }),
      error:   (title, description, opts = {}) => push({ ...opts, tone: "danger", title, description }),
      warning: (title, description, opts = {}) => push({ ...opts, tone: "warning", title, description }),
      info:    (title, description, opts = {}) => push({ ...opts, tone: "default", title, description }),
      dismiss,
    }),
    [push, dismiss]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // No-op fallback so components don't crash if used outside provider in tests.
    return {
      toast: () => {},
      success: () => {},
      error: () => {},
      warning: () => {},
      info: () => {},
      dismiss: () => {},
    };
  }
  return ctx;
}

const ICONS = {
  success: <CheckCircle2 size={18} className="text-success-400" />,
  warning: <AlertTriangle size={18} className="text-warning-400" />,
  danger:  <XCircle size={18} className="text-danger-400" />,
  default: <Info size={18} className="text-brand-300" />,
};

function ToastViewport({ toasts, onDismiss }) {
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[1000] flex flex-col gap-2 w-[min(380px,calc(100vw-2rem))]">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }) {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (toast.duration <= 0) return;
    const t = setTimeout(() => setLeaving(true), Math.max(0, toast.duration - 200));
    return () => clearTimeout(t);
  }, [toast.duration]);

  return (
    <div
      role="status"
      className={cn(
        "pointer-events-auto rounded-xl border border-border-strong bg-bg-elevated/95 backdrop-blur",
        "shadow-card overflow-hidden",
        "animate-slide-up",
        leaving && "opacity-0 transition-opacity duration-200"
      )}
    >
      <div className="flex items-start gap-3 p-3">
        <div className="mt-0.5 shrink-0">{ICONS[toast.tone] || ICONS.default}</div>
        <div className="flex-1 min-w-0">
          {toast.title && (
            <div className="text-sm font-medium text-ink truncate">{toast.title}</div>
          )}
          {toast.description && (
            <div className="text-xs text-ink-muted mt-0.5 break-words">{toast.description}</div>
          )}
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="shrink-0 text-ink-subtle hover:text-ink p-1 rounded-md hover:bg-white/[0.05]"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
